import {
  useGetPlanListForEdit,
} from "../../../hooks";
import {
  planFilters,
  PlanListItem,
} from "@features/onboarding/utility-setup/types";
import { GenericTable } from "@shared/components/GenericTable";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { MODAL_PAGE_SIZE, PAGE_SIZE } from "@shared/utils/constants";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FilterPanel } from "../../landing/Filter";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";

const SelectRecords: React.FC<any> = ({
  data,
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const remoteUtilityId = useRemoteUtilityId();
  const itemsPerPage = MODAL_PAGE_SIZE;

  const [selectedRows, setSelectedRows] = useState<string[]>(data?.selectedRecords || []);

  useEffect(() => {
    if (data?.selectedRecords) {
      setSelectedRows(data.selectedRecords);
    }
  }, [data?.selectedRecords]);

  const getFiltersFromUrl = () => {
    const page = parseInt(searchParams.get("page") || "1");
    const query = searchParams.get("query") || "";
    const category = searchParams.get("category") || "";
    const utilityService = searchParams.get("utilityService") || "";
    return {
      page,
      limit: itemsPerPage,
      remote_utility_id: Number(remoteUtilityId),
      search_data: query,
      category: category,
      utilityService: utilityService,
    };
  };

  const [filters, setFilters] = useState(getFiltersFromUrl);

  useEffect(() => {
    setFilters(getFiltersFromUrl());
  }, [searchParams, remoteUtilityId]);

  const {
    data: planData,
  } = useGetPlanListForEdit(filters);
  const columns: TableColumn<PlanListItem>[] = [
    {
      key: "planName",
      header: "Plan Name",
      width: "w-64",
      render: (value) => (
        <span className="text-sm font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: "category",
      header: "Category",
      width: "w-48",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "billingFrequencyDisplay",
      header: "Frequency",
      width: "w-40",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "validity",
      header: "Validity",
      width: "w-40",
      render: (value) => (
        <span className="text-sm text-gray-700">{value ?? "Ongoing"}</span>
      ),
    },
    {
      key: "utilityService",
      header: "Utility Service",
      width: "w-64",
      render: (services) => {
        if (!services) return <span className="text-sm text-gray-700">-</span>;
        const serviceArray = Array.isArray(services) ? services : [services];
        return (
          <div className="flex flex-wrap gap-1">
            {serviceArray.map((service: string, i: number) => (
              <Badge key={i} variant="secondary" className="font-normal text-xs">
                {service}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      key: "statusDisplay",
      header: "Status",
      width: "w-36",
      render: (value: string) => (
        <Badge variant="outline" className={`font-normal capitalize ${getStatusBadgeClass(value)}`}>
          {value || "Unknown"}
        </Badge>
      ),
    },
  ];

  const handleApplyFilters = (newFilters: Partial<planFilters>) => {
    const params = new URLSearchParams(searchParams);

    params.delete("category");
    params.delete("utilityService");
    params.delete("page");

    if (newFilters.category) {
      params.set("category", newFilters.category);
    }
    if (newFilters.utilityService) {
      params.set("utilityService", newFilters.utilityService);
    }

    params.set("page", "1");
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("page");
    params.delete("query");
    params.delete("utilityService");
    params.delete("category");
    setSearchParams(params);
  };

  const handleSearch = (searchTerm: string, paramName: string) => {
    const params = new URLSearchParams(searchParams);
    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
      params.set("page", "1");
    } else {
      params.delete(paramName);
    }
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  const handleSelectionChange = (newSelectedRows: string[]) => {
    setSelectedRows(newSelectedRows);
    if (onDataChange) {
      onDataChange({ ...data, selectedRecords: newSelectedRows });
    }
  };

  const currentPage = parseInt(searchParams.get("page") || "1");

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-md flex items-center justify-between">
        <span className="font-medium">
          {selectedRows.length} plan{selectedRows.length !== 1 ? "s" : ""} selected
        </span>
      </div>
      
      <div className="space-y-4">
        <GenericTable
          data={planData?.result || []}
          columns={columns}
          selectable={true}
          selectedRows={selectedRows}
          onSelectionChange={handleSelectionChange}
          searchPlaceholder="Search plans..."
          searchParamName="query"
          searchDebounceMs={300}
          onSearch={handleSearch}
          rowKey="id"
          pagination={{
            currentPage,
            totalPages: Math.ceil((planData?.count || 0) / itemsPerPage),
            totalItems: planData?.count || 0,
            itemsPerPage,
            startItem: (currentPage - 1) * itemsPerPage + 1,
            endItem: Math.min(currentPage * itemsPerPage, planData?.count || 0),
          }}
          onPageChange={handlePageChange}
          filterPanel={
            <FilterPanel
              currentFilters={filters}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
            />
          }
          emptyMessage="No plans found"
        />
      </div>

      <div className="flex justify-between space-x-3 mt-8 border-t pt-6">
        {!isFirstStep && (
          <Button
            type="button"
            onClick={onPrevious}
            className=""
          >
            Previous
          </Button>
        )}
        <Button
          type="button"
          onClick={onNext}
          className=" ml-auto"
        >
          {isLastStep ? "Finish" : "Next"}
        </Button>
      </div>
    </div>
  );
};

export default SelectRecords;
